import React, { memo } from "react";
import { useMediaQuery } from "@chakra-ui/react";
import { Link, useHistory } from "react-router-dom";
import { AppContainer, Col, Row, UiBox, UiIcon, UiImage, UiLink, UiText } from "~/components/elements/index";
import { QUERY_MOBILE, QUERY_TABLET } from "~/constants";
import { menu, socialPages } from "~/constants/footer";
import { IconType } from "react-icons";
import { sharedStyle } from "~/theme/shared-style";
import moment from "moment";
import { appConfig } from "~/config";
import images from "~/assets/img";

const SEPARATOR = "/";

interface MenuProps {
  title: string;
  pages: {
    url: string;
    title: string;
    icon?: IconType;
    key: number;
    mobileUrl?: string;
  }[];
}

function Menu({ title, pages }: MenuProps) {
  return (
    <Col flex="1" wordBreak="break-word">
      <UiText fontSize={{ base: "20px" }} fontWeight="bold">
        {title}
      </UiText>
      <Col mt={{ base: "10px", lg: "5" }} fontSize="md">
        {pages.map(page => {
          const isRelativePath = page.url.charAt(0) === SEPARATOR;
          if (isRelativePath) {
            return (
              <UiLink key={page.key} as={Link} to={page.url} mb="5px">
                {page.title}
              </UiLink>
            );
          }
          return (
            <UiLink key={page.key} href={page.url} mb="5px">
              {page.title}
            </UiLink>
          );
        })}
      </Col>
    </Col>
  );
}

export const Footer = memo(
  React.forwardRef<HTMLDivElement>((props, ref) => {
    const history = useHistory();
    const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`, {
      ssr: false,
    });
    const [isTabletOrLarger] = useMediaQuery(`(min-width: ${QUERY_TABLET})`, {
      ssr: false,
    });

    return (
      <UiBox p={{ base: "20px 0px", lg: "40px 0px" }}>
        <AppContainer mx="auto" h="100%" color="black" maxW={sharedStyle.pageMaxWidth}>
          <UiBox rowGap={10} as={isDesktop ? Row : Col}>
            <Col flex={0.5}>
              <Row mb={5} alignItems="center">
                <UiLink
                  href={appConfig.homepage}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <UiImage src={images.snapglogo} width="43px" height="31px" objectFit="cover" />
                </UiLink>
                <UiLink
                  href={appConfig.homepage}
                  ml={2}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <UiText _hover={{ cursor: "pointer" }} ml={1} fontSize="20px" fontWeight="800">
                    {appConfig.siteName}
                  </UiText>
                </UiLink>
              </Row>
              <UiBox as={isDesktop ? Col : Row} columnGap="2px">
                <UiText fontSize="14px">
                  Copyright © {moment().year()} {appConfig.siteName}.
                </UiText>
                <UiText fontSize="14px">All rights reserved.</UiText>
              </UiBox>
            </Col>
            <UiBox as={isTabletOrLarger ? Row : Col} columnGap={2} rowGap={isTabletOrLarger ? 0 : 8} flex={1}>
              {menu.map(value => (
                <Col key={value.key} flex={1}>
                  <Menu title={value.title} pages={value.pages} />
                  {value.title === "Contact" && (
                    <Row alignItems="center" height="fit-content" flex={0.3}>
                      {socialPages.map(page => (
                        <UiIcon
                          key={page.key}
                          as={page.icon}
                          boxSize={page.size}
                          cursor="pointer"
                          mr={4}
                          mx={0}
                          onClick={() => window.open(page.url)}
                          color="black"
                        />
                      ))}
                    </Row>
                  )}
                </Col>
              ))}
            </UiBox>
          </UiBox>
        </AppContainer>
      </UiBox>
    );
  })
);
